import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Send, Plus, Eye, Trash2, Copy, Edit2,
  FileText, Sparkles, Download, BarChart3, Lock,
  LayoutGrid, List, Search, Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import Modal from '../../components/ui/Modal';
import './EmailCampaigns.css';

const TABS = [
  { id: 'campaigns', label: 'Campaigns', icon: Mail },
  { id: 'templates', label: 'Templates', icon: FileText },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
];

const STATUS_OPTIONS = [
  { value: 'all',       label: 'All' },
  { value: 'SENT',      label: 'Sent' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'DRAFT',     label: 'Drafts' },
];

const STATUS_PILL = {
  DRAFT:     'ec-pill-muted',
  SCHEDULED: 'ec-pill-warn',
  SENDING:   'ec-pill-info',
  SENT:      'ec-pill-success',
};

const INITIAL_CAMPAIGN = {
  name: '',
  type: 'EMAIL',
  audience: 'MERCHANT_CUSTOMERS',
  templateId: '',
  subject: '',
  content: '',
  senderName: '',
  senderEmail: '',
  scheduledFor: '',
  tags: [],
  segments: [],
  targeting: { type: 'all' },
};

const INITIAL_TEMPLATE = {
  name: '',
  subject: '',
  content: '',
  description: '',
  category: 'GENERAL',
};

export default function EmailCampaigns() {
  const { getAxios } = useSecureAuth();
  const toast = useToast();
  const { confirm } = useConfirm();

  // Email is gated under `marketing.email` (FeatureGate wraps the page).
  // The analytics tab makes an extra backend call to /campaigns/analytics
  // which requires its own `analytics.email_campaigns` feature. Without
  // the gate the tab would just throw a 403; with it we show a locked
  // state and skip the fetch.
  const { hasAccess: hasAnalyticsAccess, loading: analyticsAccessLoading } =
    useFeatureAccess('analytics.email_campaigns');

  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [availableSegments, setAvailableSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [campaignForm, setCampaignForm] = useState(INITIAL_CAMPAIGN);
  const [templateForm, setTemplateForm] = useState(INITIAL_TEMPLATE);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  /* ── Fetchers ────────────────────────────────────────── */
  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAxios().get('/merchants/email-campaigns');
      if (res.data.success) setCampaigns(res.data.campaigns || []);
    } catch (err) {
      console.error('Fetch campaigns error:', err);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [getAxios, toast]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await getAxios().get('/merchants/email-templates');
      if (res.data.success) setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Fetch templates error:', err);
    }
  }, [getAxios]);

  const fetchTags = useCallback(async () => {
    try {
      const res = await getAxios().get('/merchants/customers/tags');
      if (res.data.success) setAvailableTags(res.data.tags || []);
    } catch (err) {
      console.error('Fetch tags error:', err);
    }
  }, [getAxios]);

  const fetchSegments = useCallback(async () => {
    try {
      const res = await getAxios().get('/merchants/segments');
      if (res.data.success) setAvailableSegments(res.data.segments || []);
    } catch (err) {
      console.error('Fetch segments error:', err);
    }
  }, [getAxios]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      const res = await getAxios().get('/merchants/campaigns/analytics');
      if (res.data.success) setAnalytics(res.data);
    } catch (err) {
      console.error('Fetch analytics error:', err);
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [getAxios, toast]);

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchTags();
    fetchSegments();
  }, [fetchCampaigns, fetchTemplates, fetchTags, fetchSegments]);

  useEffect(() => {
    if (activeTab === 'analytics' && hasAnalyticsAccess) fetchAnalytics();
  }, [activeTab, hasAnalyticsAccess, fetchAnalytics]);

  /* ── Validation ──────────────────────────────────────── */
  const validateCampaignForm = () => {
    const errors = {};
    if (!campaignForm.name.trim())    errors.name    = 'Campaign name is required';
    if (!campaignForm.subject.trim()) errors.subject = 'Subject is required';
    if (!campaignForm.content.trim()) errors.content = 'Content is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateTemplateForm = () => {
    const errors = {};
    if (!templateForm.name.trim())    errors.name    = 'Template name is required';
    if (!templateForm.subject.trim()) errors.subject = 'Subject is required';
    if (!templateForm.content.trim()) errors.content = 'Content is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ── Mutations ───────────────────────────────────────── */
  const handleSaveCampaign = async () => {
    if (!validateCampaignForm()) return;
    setSaving(true);
    try {
      const res = await getAxios().post('/merchants/email-campaigns/send', campaignForm);
      if (res.data.success) {
        toast.success(selectedCampaign ? 'Campaign updated' : 'Campaign created');
        closeCampaignModal();
        fetchCampaigns();
      } else {
        toast.error(res.data.error || 'Failed to save campaign');
      }
    } catch (err) {
      console.error('Save campaign error:', err);
      toast.error(err.response?.data?.error || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!validateTemplateForm()) return;
    setSaving(true);
    try {
      const url = selectedTemplate
        ? `/merchants/email-templates/${selectedTemplate.id}`
        : '/merchants/email-templates';
      const method = selectedTemplate ? 'put' : 'post';
      const res = await getAxios()[method](url, templateForm);
      if (res.data.success) {
        toast.success(selectedTemplate ? 'Template updated' : 'Template created');
        closeTemplateModal();
        fetchTemplates();
      } else {
        toast.error(res.data.error || 'Failed to save template');
      }
    } catch (err) {
      console.error('Save template error:', err);
      toast.error(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    const ok = await confirm('Delete this template? This cannot be undone.', {
      title: 'Delete template?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
    });
    if (!ok) return;
    try {
      const res = await getAxios().delete(`/merchants/email-templates/${templateId}`);
      if (res.data.success) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        toast.error(res.data.error || 'Failed to delete template');
      }
    } catch (err) {
      console.error('Delete template error:', err);
      toast.error(err.response?.data?.error || 'Failed to delete template');
    }
  };

  /* ── UI handlers ─────────────────────────────────────── */
  const openCreateCampaign = () => {
    setSelectedCampaign(null);
    setCampaignForm(INITIAL_CAMPAIGN);
    setFormErrors({});
    setShowCreateModal(true);
  };

  const handleEditCampaign = (campaign) => {
    setCampaignForm({
      name: campaign.name,
      type: campaign.type,
      audience: 'MERCHANT_CUSTOMERS',
      templateId: campaign.templateId || '',
      subject: campaign.subject,
      content: campaign.content,
      senderName: campaign.senderName || '',
      senderEmail: campaign.senderEmail || '',
      scheduledFor: campaign.scheduledFor ? new Date(campaign.scheduledFor).toISOString().slice(0, 16) : '',
      tags: campaign.tags || [],
      segments: campaign.segments || [],
      targeting: {
        type: (campaign.segments && campaign.segments.length > 0) ? 'segments'
            : (campaign.tags && campaign.tags.length > 0) ? 'tags' : 'all',
      },
    });
    setSelectedCampaign(campaign);
    setFormErrors({});
    setShowCreateModal(true);
  };

  const handleDuplicateCampaign = (campaign) => {
    setCampaignForm({
      ...INITIAL_CAMPAIGN,
      name: `${campaign.name} (Copy)`,
      type: campaign.type,
      templateId: campaign.templateId || '',
      subject: campaign.subject,
      content: campaign.content,
      senderName: campaign.senderName || '',
      senderEmail: campaign.senderEmail || '',
      tags: campaign.tags || [],
      segments: campaign.segments || [],
      targeting: {
        type: (campaign.segments && campaign.segments.length > 0) ? 'segments'
            : (campaign.tags && campaign.tags.length > 0) ? 'tags' : 'all',
      },
    });
    setSelectedCampaign(null);
    setFormErrors({});
    setShowCreateModal(true);
  };

  const handleViewCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setShowViewModal(true);
  };

  const openCreateTemplate = () => {
    setSelectedTemplate(null);
    setTemplateForm(INITIAL_TEMPLATE);
    setFormErrors({});
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template) => {
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      content: template.content,
      description: template.description || '',
      category: template.category || 'GENERAL',
    });
    setSelectedTemplate(template);
    setFormErrors({});
    setShowTemplateModal(true);
  };

  const handleUseTemplate = (template) => {
    setCampaignForm({
      ...campaignForm,
      templateId: template.id,
      subject: template.subject,
      content: template.content,
    });
    setShowTemplateModal(false);
    setShowCreateModal(true);
  };

  const closeCampaignModal = () => {
    setShowCreateModal(false);
    setSelectedCampaign(null);
    setCampaignForm(INITIAL_CAMPAIGN);
    setFormErrors({});
  };

  const closeTemplateModal = () => {
    setShowTemplateModal(false);
    setSelectedTemplate(null);
    setTemplateForm(INITIAL_TEMPLATE);
    setFormErrors({});
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedCampaign(null);
  };

  const exportAnalytics = () => {
    if (!analytics || !analytics.campaigns || analytics.campaigns.length === 0) {
      toast.warning('No analytics data to export');
      return;
    }
    const headers = ['Campaign Name', 'Type', 'Sent Date', 'Recipients', 'Opened', 'Clicked', 'Open Rate (%)', 'Click Rate (%)'];
    const rows = analytics.campaigns.map((c) => [
      c.name, c.type, new Date(c.sentAt).toLocaleDateString(),
      c.stats.sent, c.stats.opened, c.stats.clicked, c.stats.openRate, c.stats.clickRate,
    ]);

    let csv = headers.join(',') + '\n';
    rows.forEach((row) => { csv += row.map((cell) => `"${cell}"`).join(',') + '\n'; });
    csv += '\n\nOverall Statistics\n';
    csv += `Total Emails Sent,${analytics.overall.totalSent}\n`;
    csv += `Total Opened,${analytics.overall.totalOpened}\n`;
    csv += `Total Clicked,${analytics.overall.totalClicked}\n`;
    csv += `Average Open Rate (%),${analytics.overall.avgOpenRate}\n`;
    csv += `Average Click Rate (%),${analytics.overall.avgClickRate}\n`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `email-campaign-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ── Derived ──────────────────────────────────────────── */
  const filteredCampaigns = campaigns.filter((c) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = c.name.toLowerCase().includes(q) || c.subject.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredTemplates = templates.filter((t) => {
    const q = searchQuery.toLowerCase();
    return t.name.toLowerCase().includes(q) || t.subject.toLowerCase().includes(q);
  });

  const stats = {
    total:     campaigns.length,
    sent:      campaigns.filter((c) => c.status === 'SENT').length,
    scheduled: campaigns.filter((c) => c.status === 'SCHEDULED').length,
    draft:     campaigns.filter((c) => c.status === 'DRAFT').length,
  };

  const headerActions = (
    <div className="ec-header-actions">
      {activeTab === 'templates' && (
        <button type="button" className="ec-btn ec-btn-secondary" onClick={openCreateTemplate}>
          <Plus size={14} /> <span className="ec-btn-text">New template</span>
        </button>
      )}
      {activeTab !== 'analytics' && (
        <button type="button" className="ec-btn ec-btn-primary" onClick={openCreateCampaign}>
          <Plus size={14} /> <span className="ec-btn-text">New campaign</span>
        </button>
      )}
    </div>
  );

  /* ── Render ──────────────────────────────────────────── */
  return (
    <Shell>
      <FeatureGate
        featureId="marketing.email"
        upgradeMessage="Upgrade to access email campaigns and marketing automation"
      >
        <div className="ec-page">

          {/* ── Header ─────────────────────────────────── */}
          <header className="ec-header">
            <div className="ec-header-text">
              <h1 className="ec-title">Email campaigns</h1>
              <p className="ec-subtitle">Create and send email campaigns to your customers.</p>
            </div>
            {headerActions}
          </header>

          {/* ── Tabs (pill style, scrolls horizontally on mobile) ── */}
          <div className="ec-tabs-wrap">
            <div className="ec-tabs">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`ec-tab${activeTab === t.id ? ' is-active' : ''}`}
                    onClick={() => setActiveTab(t.id)}
                  >
                    <Icon size={14} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Campaigns tab ──────────────────────────── */}
          {activeTab === 'campaigns' && (
            <>
              <section className="ec-stat-grid">
                <div className="ec-stat">
                  <span className="ec-stat-label">Total</span>
                  <span className="ec-stat-value">{stats.total}</span>
                </div>
                <div className="ec-stat">
                  <span className="ec-stat-label">Sent</span>
                  <span className="ec-stat-value ec-stat-value-success">{stats.sent}</span>
                </div>
                <div className="ec-stat">
                  <span className="ec-stat-label">Scheduled</span>
                  <span className="ec-stat-value ec-stat-value-warn">{stats.scheduled}</span>
                </div>
                <div className="ec-stat">
                  <span className="ec-stat-label">Drafts</span>
                  <span className="ec-stat-value">{stats.draft}</span>
                </div>
              </section>

              <div className="ec-filters">
                <div className="ec-search">
                  <Search size={14} className="ec-search-icon" />
                  <input
                    type="text"
                    className="ec-input ec-search-input"
                    placeholder="Search campaigns…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <select
                  className="ec-input ec-select ec-filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <div className="ec-view-toggle" role="group" aria-label="View mode">
                  <button
                    type="button"
                    className={`ec-view-btn${viewMode === 'grid' ? ' is-active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid view"
                    aria-label="Grid view"
                  >
                    <LayoutGrid size={16} />
                  </button>
                  <button
                    type="button"
                    className={`ec-view-btn${viewMode === 'list' ? ' is-active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List view"
                    aria-label="List view"
                  >
                    <List size={16} />
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="ec-loading">
                  <Loader2 size={20} className="ec-spin" /> Loading campaigns…
                </div>
              ) : filteredCampaigns.length === 0 ? (
                <div className="ec-empty">
                  <div className="ec-empty-icon"><Mail size={22} /></div>
                  <h3 className="ec-empty-title">No campaigns found</h3>
                  <p className="ec-empty-desc">
                    {searchQuery || statusFilter !== 'all'
                      ? 'Try a different search or filter.'
                      : 'Create your first email campaign to reach your customers.'}
                  </p>
                  {!searchQuery && statusFilter === 'all' && (
                    <button type="button" className="ec-btn ec-btn-primary" onClick={openCreateCampaign}>
                      <Plus size={14} /> Create campaign
                    </button>
                  )}
                </div>
              ) : viewMode === 'grid' ? (
                <div className="ec-grid">
                  {filteredCampaigns.map((c) => (
                    <article key={c.id} className="ec-card">
                      <header className="ec-card-head">
                        <h3 className="ec-card-title">{c.name}</h3>
                        <span className={`ec-pill ${STATUS_PILL[c.status] || 'ec-pill-muted'}`}>
                          {c.status}
                        </span>
                      </header>
                      <p className="ec-card-meta">
                        <Mail size={12} /> {c.type} · {new Date(c.createdAt).toLocaleDateString()}
                      </p>
                      <div className="ec-mini-stats">
                        <div><strong>{c.recipientCount || 0}</strong><span>Recipients</span></div>
                        <div><strong>{c.openRate || 0}%</strong><span>Open rate</span></div>
                        <div><strong>{c.clickRate || 0}%</strong><span>Click rate</span></div>
                      </div>
                      <div className="ec-card-actions">
                        <button type="button" className="ec-btn ec-btn-ghost" onClick={() => handleViewCampaign(c)}>
                          <Eye size={13} /> View
                        </button>
                        <button type="button" className="ec-btn ec-btn-ghost" onClick={() => handleEditCampaign(c)}>
                          <Edit2 size={13} /> Edit
                        </button>
                        <button type="button" className="ec-btn ec-btn-ghost" onClick={() => handleDuplicateCampaign(c)}>
                          <Copy size={13} /> Duplicate
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <ul className="ec-list">
                  {filteredCampaigns.map((c) => (
                    <li key={c.id} className="ec-row">
                      <div className="ec-list-row">
                        <span className="ec-list-name">{c.name}</span>
                        <span className={`ec-pill ${STATUS_PILL[c.status] || 'ec-pill-muted'}`}>
                          {c.status}
                        </span>
                        <span className="ec-list-meta">
                          {c.type} · {new Date(c.createdAt).toLocaleDateString()}
                        </span>
                        <span className="ec-list-stats">
                          <strong>{c.recipientCount || 0}</strong> recipients ·
                          {' '}{c.openRate || 0}% open · {c.clickRate || 0}% click
                        </span>
                        <span className="ec-list-actions">
                          <button type="button" className="ec-icon-btn" onClick={() => handleViewCampaign(c)} aria-label="View">
                            <Eye size={14} />
                          </button>
                          <button type="button" className="ec-icon-btn" onClick={() => handleEditCampaign(c)} aria-label="Edit">
                            <Edit2 size={14} />
                          </button>
                          <button type="button" className="ec-icon-btn" onClick={() => handleDuplicateCampaign(c)} aria-label="Duplicate">
                            <Copy size={14} />
                          </button>
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {/* ── Templates tab ──────────────────────────── */}
          {activeTab === 'templates' && (
            <>
              <div className="ec-filters">
                <div className="ec-search">
                  <Search size={14} className="ec-search-icon" />
                  <input
                    type="text"
                    className="ec-input ec-search-input"
                    placeholder="Search templates…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="ec-view-toggle" role="group" aria-label="View mode">
                  <button
                    type="button"
                    className={`ec-view-btn${viewMode === 'grid' ? ' is-active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid view"
                    aria-label="Grid view"
                  >
                    <LayoutGrid size={16} />
                  </button>
                  <button
                    type="button"
                    className={`ec-view-btn${viewMode === 'list' ? ' is-active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List view"
                    aria-label="List view"
                  >
                    <List size={16} />
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="ec-loading">
                  <Loader2 size={20} className="ec-spin" /> Loading templates…
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="ec-empty">
                  <div className="ec-empty-icon"><FileText size={22} /></div>
                  <h3 className="ec-empty-title">No templates found</h3>
                  <p className="ec-empty-desc">
                    {searchQuery
                      ? 'Try a different search.'
                      : 'Create reusable email templates to speed up campaign creation.'}
                  </p>
                  {!searchQuery && (
                    <button type="button" className="ec-btn ec-btn-primary" onClick={openCreateTemplate}>
                      <Plus size={14} /> Create template
                    </button>
                  )}
                </div>
              ) : viewMode === 'grid' ? (
                <div className="ec-grid">
                  {filteredTemplates.map((t) => (
                    <article key={t.id} className="ec-card">
                      <header className="ec-card-head">
                        <h3 className="ec-card-title">{t.name}</h3>
                        <span className="ec-pill ec-pill-muted">{t.category || 'GENERAL'}</span>
                      </header>
                      <p className="ec-card-meta">
                        <FileText size={12} /> {new Date(t.createdAt).toLocaleDateString()}
                      </p>
                      <div className="ec-template-preview">
                        <span className="ec-template-subject"><strong>Subject:</strong> {t.subject}</span>
                        <span className="ec-template-content">{t.content.substring(0, 140)}…</span>
                      </div>
                      <div className="ec-card-actions">
                        <button type="button" className="ec-btn ec-btn-ghost" onClick={() => handleUseTemplate(t)}>
                          <Sparkles size={13} /> Use
                        </button>
                        <button type="button" className="ec-btn ec-btn-ghost" onClick={() => handleEditTemplate(t)}>
                          <Edit2 size={13} /> Edit
                        </button>
                        <button type="button" className="ec-btn ec-btn-ghost ec-btn-danger" onClick={() => handleDeleteTemplate(t.id)}>
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <ul className="ec-list">
                  {filteredTemplates.map((t) => (
                    <li key={t.id} className="ec-row">
                      <div className="ec-list-row">
                        <span className="ec-list-name">{t.name}</span>
                        <span className="ec-pill ec-pill-muted">{t.category || 'GENERAL'}</span>
                        <span className="ec-list-meta">{new Date(t.createdAt).toLocaleDateString()}</span>
                        <span className="ec-list-stats">{t.subject}</span>
                        <span className="ec-list-actions">
                          <button type="button" className="ec-icon-btn" onClick={() => handleUseTemplate(t)} aria-label="Use template">
                            <Sparkles size={14} />
                          </button>
                          <button type="button" className="ec-icon-btn" onClick={() => handleEditTemplate(t)} aria-label="Edit template">
                            <Edit2 size={14} />
                          </button>
                          <button type="button" className="ec-icon-btn ec-icon-btn-danger" onClick={() => handleDeleteTemplate(t.id)} aria-label="Delete template">
                            <Trash2 size={14} />
                          </button>
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {/* ── Analytics tab ──────────────────────────── */}
          {activeTab === 'analytics' && (
            <>
              {analyticsAccessLoading ? (
                <div className="ec-loading">
                  <Loader2 size={20} className="ec-spin" /> Loading…
                </div>
              ) : !hasAnalyticsAccess ? (
                <div className="ec-locked">
                  <div className="ec-locked-icon"><Lock size={20} /></div>
                  <h3 className="ec-locked-title">Email analytics is a Premium feature</h3>
                  <p className="ec-locked-desc">
                    Track open rates, click rates, and per-campaign performance on the Premium plan.
                  </p>
                  <Link to="/settings?tab=billing" className="ec-btn ec-btn-primary">
                    Upgrade plan
                  </Link>
                </div>
              ) : analyticsLoading ? (
                <div className="ec-loading">
                  <Loader2 size={20} className="ec-spin" /> Loading analytics…
                </div>
              ) : analytics && analytics.overall ? (
                <>
                  <section className="ec-stat-grid">
                    <div className="ec-stat">
                      <span className="ec-stat-label">Total sent</span>
                      <span className="ec-stat-value">{analytics.overall.totalSent}</span>
                    </div>
                    <div className="ec-stat">
                      <span className="ec-stat-label">Total opened</span>
                      <span className="ec-stat-value ec-stat-value-success">{analytics.overall.totalOpened}</span>
                    </div>
                    <div className="ec-stat">
                      <span className="ec-stat-label">Avg open rate</span>
                      <span className="ec-stat-value">{analytics.overall.avgOpenRate}%</span>
                    </div>
                    <div className="ec-stat">
                      <span className="ec-stat-label">Avg click rate</span>
                      <span className="ec-stat-value ec-stat-value-info">{analytics.overall.avgClickRate}%</span>
                    </div>
                  </section>

                  <section className="ec-card-block">
                    <div className="ec-block-head">
                      <h3 className="ec-block-title">Campaign performance</h3>
                      <button type="button" className="ec-btn ec-btn-secondary" onClick={exportAnalytics}>
                        <Download size={13} /> <span className="ec-btn-text">Export</span>
                      </button>
                    </div>

                    {analytics.campaigns && analytics.campaigns.length > 0 ? (
                      <ul className="ec-list">
                        {analytics.campaigns.map((c) => (
                          <li key={c.campaignId} className="ec-row">
                            <div className="ec-perf-row">
                              <span className="ec-perf-name">{c.name}</span>
                              <span className="ec-pill ec-pill-muted">{c.type}</span>
                              <span className="ec-perf-date">
                                {new Date(c.sentAt).toLocaleDateString()}
                              </span>
                              <span className="ec-perf-recipients">
                                <strong>{c.stats.sent}</strong> sent · {c.stats.opened} opened · {c.stats.clicked} clicked
                              </span>
                              <span className="ec-perf-rates">
                                <span className={`ec-rate${c.stats.openRate > 20 ? ' is-good' : ''}`}>
                                  {c.stats.openRate}% open
                                </span>
                                <span className={`ec-rate${c.stats.clickRate > 5 ? ' is-good' : ''}`}>
                                  {c.stats.clickRate}% click
                                </span>
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="ec-empty-inline">No sent campaigns yet.</div>
                    )}
                  </section>
                </>
              ) : (
                <div className="ec-empty">
                  <div className="ec-empty-icon"><BarChart3 size={22} /></div>
                  <h3 className="ec-empty-title">No analytics yet</h3>
                  <p className="ec-empty-desc">Send your first campaign to see open and click data here.</p>
                </div>
              )}
            </>
          )}

          {/* ── Create/Edit Campaign Modal ─────────────── */}
          <Modal
            isOpen={showCreateModal}
            onClose={closeCampaignModal}
            title={selectedCampaign ? 'Edit campaign' : 'New campaign'}
            size="lg"
            padded={false}
            footer={
              <>
                <button type="button" className="ec-btn ec-btn-secondary" onClick={closeCampaignModal}>Cancel</button>
                <button
                  type="submit"
                  form="ec-campaign-form"
                  className="ec-btn ec-btn-primary"
                  disabled={saving}
                >
                  {saving ? <Loader2 size={14} className="ec-spin" /> : <Send size={14} />}
                  {saving ? 'Saving…' : (campaignForm.scheduledFor ? 'Schedule campaign' : 'Send now')}
                </button>
              </>
            }
          >
            <form
              id="ec-campaign-form"
              onSubmit={(e) => { e.preventDefault(); handleSaveCampaign(); }}
              className="ec-form"
            >
              <div className="ec-form-row">
                <div className="ec-field">
                  <label className="ec-field-label">Campaign name *</label>
                  <input
                    type="text"
                    className={`ec-input${formErrors.name ? ' is-error' : ''}`}
                    placeholder="e.g. Spring sale 2026"
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  />
                  {formErrors.name && <span className="ec-field-error">{formErrors.name}</span>}
                </div>
                <div className="ec-field">
                  <label className="ec-field-label">Type *</label>
                  <select
                    className="ec-input ec-select"
                    value={campaignForm.type}
                    onChange={(e) => setCampaignForm({ ...campaignForm, type: e.target.value })}
                  >
                    <option value="EMAIL">Email</option>
                    <option value="IN_APP">In-app notification</option>
                  </select>
                </div>
              </div>

              <div className="ec-field">
                <label className="ec-field-label">Start from template (optional)</label>
                <select
                  className="ec-input ec-select"
                  value={campaignForm.templateId}
                  onChange={(e) => {
                    const t = templates.find((x) => x.id === parseInt(e.target.value));
                    if (t) {
                      setCampaignForm({
                        ...campaignForm,
                        templateId: e.target.value,
                        subject: t.subject,
                        content: t.content,
                      });
                    } else {
                      setCampaignForm({ ...campaignForm, templateId: '' });
                    }
                  }}
                >
                  <option value="">— Start from scratch —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="ec-field">
                <label className="ec-field-label">Subject line *</label>
                <input
                  type="text"
                  className={`ec-input${formErrors.subject ? ' is-error' : ''}`}
                  placeholder="e.g. Don't miss our spring sale!"
                  value={campaignForm.subject}
                  onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })}
                />
                {formErrors.subject && <span className="ec-field-error">{formErrors.subject}</span>}
              </div>

              {campaignForm.type === 'EMAIL' && (
                <div className="ec-form-row">
                  <div className="ec-field">
                    <label className="ec-field-label">Sender name</label>
                    <input
                      type="text"
                      className="ec-input"
                      placeholder="e.g. Your store name"
                      value={campaignForm.senderName}
                      onChange={(e) => setCampaignForm({ ...campaignForm, senderName: e.target.value })}
                    />
                  </div>
                  <div className="ec-field">
                    <label className="ec-field-label">Reply-to email</label>
                    <input
                      type="email"
                      className="ec-input"
                      placeholder="hello@yourstore.com"
                      value={campaignForm.senderEmail}
                      onChange={(e) => setCampaignForm({ ...campaignForm, senderEmail: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div className="ec-field">
                <label className="ec-field-label">Message content *</label>
                <textarea
                  className={`ec-input ec-textarea${formErrors.content ? ' is-error' : ''}`}
                  placeholder="Enter your email content here…"
                  value={campaignForm.content}
                  onChange={(e) => setCampaignForm({ ...campaignForm, content: e.target.value })}
                  rows={8}
                />
                {formErrors.content
                  ? <span className="ec-field-error">{formErrors.content}</span>
                  : <span className="ec-field-hint">Use variables like {'{{'}customerName{'}}'}  for personalization.</span>}
              </div>

              <div className="ec-field">
                <label className="ec-field-label">Target audience</label>
                <select
                  className="ec-input ec-select"
                  value={campaignForm.targeting.type}
                  onChange={(e) => setCampaignForm({ ...campaignForm, targeting: { type: e.target.value } })}
                >
                  <option value="all">All customers</option>
                  <option value="tags">Specific tags</option>
                  <option value="segments">Specific segments</option>
                </select>
              </div>

              {campaignForm.targeting.type === 'tags' && (
                <div className="ec-field">
                  <label className="ec-field-label">Tags</label>
                  {availableTags.length > 0 ? (
                    <div className="ec-chip-grid">
                      {availableTags.map((tag) => {
                        const selected = campaignForm.tags.includes(tag);
                        return (
                          <label key={tag} className={`ec-chip${selected ? ' is-selected' : ''}`}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...campaignForm.tags, tag]
                                  : campaignForm.tags.filter((t) => t !== tag);
                                setCampaignForm({ ...campaignForm, tags: next });
                              }}
                            />
                            {tag}
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="ec-field-hint">No tags found. Add tags to your customers first.</span>
                  )}
                  <span className="ec-field-hint">{campaignForm.tags.length} tag(s) selected</span>
                </div>
              )}

              {campaignForm.targeting.type === 'segments' && (
                <div className="ec-field">
                  <label className="ec-field-label">Segments</label>
                  {availableSegments.length > 0 ? (
                    <div className="ec-segment-grid">
                      {availableSegments.map((seg) => {
                        const selected = campaignForm.segments.includes(seg.id);
                        return (
                          <label key={seg.id} className={`ec-segment${selected ? ' is-selected' : ''}`}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...campaignForm.segments, seg.id]
                                  : campaignForm.segments.filter((s) => s !== seg.id);
                                setCampaignForm({ ...campaignForm, segments: next });
                              }}
                            />
                            <span className="ec-segment-text">
                              <strong>{seg.name}</strong>
                              {seg.description && <em>{seg.description}</em>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="ec-field-hint">No segments found. Create customer segments first.</span>
                  )}
                  <span className="ec-field-hint">{campaignForm.segments.length} segment(s) selected</span>
                </div>
              )}

              <div className="ec-field">
                <label className="ec-field-label">Schedule send (optional)</label>
                <input
                  type="datetime-local"
                  className="ec-input"
                  value={campaignForm.scheduledFor}
                  onChange={(e) => setCampaignForm({ ...campaignForm, scheduledFor: e.target.value })}
                />
                <span className="ec-field-hint">Leave empty to send immediately.</span>
              </div>
            </form>
          </Modal>

          {/* ── Create/Edit Template Modal ─────────────── */}
          <Modal
            isOpen={showTemplateModal}
            onClose={closeTemplateModal}
            title={selectedTemplate ? 'Edit template' : 'New template'}
            size="md"
            padded={false}
            footer={
              <>
                <button type="button" className="ec-btn ec-btn-secondary" onClick={closeTemplateModal}>Cancel</button>
                <button
                  type="submit"
                  form="ec-template-form"
                  className="ec-btn ec-btn-primary"
                  disabled={saving}
                >
                  {saving ? <Loader2 size={14} className="ec-spin" /> : null}
                  {saving ? 'Saving…' : (selectedTemplate ? 'Update template' : 'Create template')}
                </button>
              </>
            }
          >
            <form
              id="ec-template-form"
              onSubmit={(e) => { e.preventDefault(); handleSaveTemplate(); }}
              className="ec-form"
            >
              <div className="ec-field">
                <label className="ec-field-label">Template name *</label>
                <input
                  type="text"
                  className={`ec-input${formErrors.name ? ' is-error' : ''}`}
                  placeholder="e.g. Welcome email"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                />
                {formErrors.name && <span className="ec-field-error">{formErrors.name}</span>}
              </div>

              <div className="ec-form-row">
                <div className="ec-field">
                  <label className="ec-field-label">Description</label>
                  <input
                    type="text"
                    className="ec-input"
                    placeholder="Brief description"
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  />
                </div>
                <div className="ec-field">
                  <label className="ec-field-label">Category</label>
                  <select
                    className="ec-input ec-select"
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                  >
                    <option value="GENERAL">General</option>
                    <option value="WELCOME">Welcome</option>
                    <option value="PROMOTIONAL">Promotional</option>
                    <option value="TRANSACTIONAL">Transactional</option>
                    <option value="NOTIFICATION">Notification</option>
                  </select>
                </div>
              </div>

              <div className="ec-field">
                <label className="ec-field-label">Subject line *</label>
                <input
                  type="text"
                  className={`ec-input${formErrors.subject ? ' is-error' : ''}`}
                  placeholder="e.g. Welcome to our store!"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                />
                {formErrors.subject && <span className="ec-field-error">{formErrors.subject}</span>}
              </div>

              <div className="ec-field">
                <label className="ec-field-label">Template content *</label>
                <textarea
                  className={`ec-input ec-textarea${formErrors.content ? ' is-error' : ''}`}
                  placeholder="Enter your template content here…"
                  value={templateForm.content}
                  onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
                  rows={10}
                />
                {formErrors.content
                  ? <span className="ec-field-error">{formErrors.content}</span>
                  : <span className="ec-field-hint">Use {'{{'}customerName{'}}'} or {'{{'}email{'}}'} for dynamic content.</span>}
              </div>
            </form>
          </Modal>

          {/* ── View Campaign Modal ─────────────────────── */}
          <Modal
            isOpen={showViewModal && !!selectedCampaign}
            onClose={closeViewModal}
            title={selectedCampaign?.name || 'Campaign'}
            size="lg"
            padded={false}
            footer={
              <>
                <button type="button" className="ec-btn ec-btn-secondary" onClick={closeViewModal}>Close</button>
                {selectedCampaign && (
                  <button
                    type="button"
                    className="ec-btn ec-btn-primary"
                    onClick={() => { closeViewModal(); handleDuplicateCampaign(selectedCampaign); }}
                  >
                    <Copy size={14} /> Duplicate
                  </button>
                )}
              </>
            }
          >
            {selectedCampaign && (
              <div className="ec-form">
                <section className="ec-view-block">
                  <h4 className="ec-view-title">Campaign details</h4>
                  <dl className="ec-kv">
                    <div className="ec-kv-row">
                      <dt>Status</dt>
                      <dd>
                        <span className={`ec-pill ${STATUS_PILL[selectedCampaign.status] || 'ec-pill-muted'}`}>
                          {selectedCampaign.status}
                        </span>
                      </dd>
                    </div>
                    <div className="ec-kv-row"><dt>Type</dt><dd>{selectedCampaign.type}</dd></div>
                    <div className="ec-kv-row"><dt>Recipients</dt><dd>{selectedCampaign.recipientCount || 0}</dd></div>
                    <div className="ec-kv-row">
                      <dt>Created</dt>
                      <dd>{new Date(selectedCampaign.createdAt).toLocaleString()}</dd>
                    </div>
                    {selectedCampaign.sentAt && (
                      <div className="ec-kv-row">
                        <dt>Sent</dt>
                        <dd>{new Date(selectedCampaign.sentAt).toLocaleString()}</dd>
                      </div>
                    )}
                  </dl>
                </section>

                <section className="ec-view-block">
                  <h4 className="ec-view-title">Email content</h4>
                  <dl className="ec-kv">
                    <div className="ec-kv-row">
                      <dt>Subject</dt>
                      <dd>{selectedCampaign.subject}</dd>
                    </div>
                  </dl>
                  <div className="ec-view-message">{selectedCampaign.content}</div>
                </section>

                <section className="ec-view-block">
                  <h4 className="ec-view-title">Performance</h4>
                  <div className="ec-stat-grid ec-stat-grid-tight">
                    <div className="ec-stat">
                      <span className="ec-stat-label">Open rate</span>
                      <span className="ec-stat-value">{selectedCampaign.openRate || 0}%</span>
                    </div>
                    <div className="ec-stat">
                      <span className="ec-stat-label">Click rate</span>
                      <span className="ec-stat-value">{selectedCampaign.clickRate || 0}%</span>
                    </div>
                  </div>
                </section>
              </div>
            )}
          </Modal>

        </div>
      </FeatureGate>
    </Shell>
  );
}
